import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { fmtEUR, fmtDate } from "../lib/format";
import { Input } from "../components/ui/input";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { MoreHorizontal, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import {
  Card, Button, SectionLabel, StatusPill,
} from "../components/app/theme";

const RISK_LEVEL = { HAUT: "HAUT", MOYEN: "MOYEN", FAIBLE: "FAIBLE" };
const RISK_LABEL = { HAUT: "Haut", MOYEN: "Moyen", FAIBLE: "Faible" };

export default function Propales() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [risque, setRisque] = useState("all");
  const [client, setClient] = useState("");
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/propales");
      setRows(r.data.propales);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((p) => {
      if (risque !== "all" && p.score_info?.niveau_risque !== risque) return false;
      if (client && !p.client.toLowerCase().includes(client.toLowerCase())) return false;
      if (min && p.montant_ht < parseFloat(min)) return false;
      if (max && p.montant_ht > parseFloat(max)) return false;
      return true;
    });
  }, [rows, risque, client, min, max]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/propales/${id}/status`, { local_status: status, reporte_jusquau: null });
      toast.success("Mise à jour effectuée");
      load();
    } catch { toast.error("Échec"); }
  };

  const inputStyle = {
    backgroundColor: "var(--th-surface)",
    border: "1px solid var(--th-border-strong)",
    borderRadius: 8,
  };

  return (
    <div className="th-scope p-6 md:p-10" style={{ backgroundColor: "var(--th-bg)" }}>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <SectionLabel>Base de données</SectionLabel>
          <h1
            className="mt-1"
            style={{
              fontFamily: "var(--th-font-serif)",
              fontSize: 27,
              fontWeight: 500,
              letterSpacing: "-0.005em",
              color: "var(--th-text)",
            }}
          >
            Propales commerciales envoyées
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--th-text-2)" }}>
            {filtered.length} sur {rows.length}
          </p>
        </div>
        <Button onClick={load} variant="secondary">
          <RefreshCcw size={14} /> Actualiser
        </Button>
      </header>

      <Card className="p-5 mb-5 grid grid-cols-2 md:grid-cols-5 gap-4">
        <div>
          <SectionLabel className="mb-1.5">Risque</SectionLabel>
          <Select value={risque} onValueChange={setRisque}>
            <SelectTrigger data-testid="filter-risque" style={inputStyle}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="HAUT">Haut</SelectItem>
              <SelectItem value="MOYEN">Moyen</SelectItem>
              <SelectItem value="FAIBLE">Faible</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <SectionLabel className="mb-1.5">Client</SectionLabel>
          <Input
            value={client}
            onChange={(e) => setClient(e.target.value)}
            placeholder="Filtrer par nom"
            style={inputStyle}
          />
        </div>
        <div>
          <SectionLabel className="mb-1.5">Montant min €</SectionLabel>
          <Input
            value={min}
            onChange={(e) => setMin(e.target.value)}
            type="number"
            style={inputStyle}
          />
        </div>
        <div>
          <SectionLabel className="mb-1.5">Montant max €</SectionLabel>
          <Input
            value={max}
            onChange={(e) => setMax(e.target.value)}
            type="number"
            style={inputStyle}
          />
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="th-table w-full">
          <thead>
            <tr>
              <th className="text-left">N°</th>
              <th className="text-left">Client</th>
              <th className="text-right">Montant HT</th>
              <th className="text-right">Envoyée le</th>
              <th className="text-right">Ancienneté</th>
              <th className="text-right">Relances</th>
              <th className="text-center">Risque</th>
              <th className="text-center">Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="p-6 text-center" style={{ color: "var(--th-text-3)" }}>
                  Chargement…
                </td>
              </tr>
            )}
            {!loading && filtered.map((p) => (
              <tr key={p.id}>
                <td className="th-num" style={{ color: "var(--th-text-2)" }}>{p.numero}</td>
                <td style={{ fontWeight: 500 }}>{p.client}</td>
                <td className="th-num text-right">{fmtEUR(p.montant_ht)}</td>
                <td className="th-num text-right" style={{ color: "var(--th-text-2)" }}>
                  {fmtDate(p.date_envoi)}
                </td>
                <td className="th-num text-right">
                  {p.score_info?.metrics?.anciennete_jours ?? "—"}j
                </td>
                <td className="th-num text-right">{p.nb_relances}</td>
                <td className="text-center">
                  {p.score_info ? (
                    <StatusPill level={RISK_LEVEL[p.score_info.niveau_risque]}>
                      {RISK_LABEL[p.score_info.niveau_risque]}
                    </StatusPill>
                  ) : (
                    <span className="text-xs" style={{ color: "var(--th-text-3)" }}>clôturée</span>
                  )}
                </td>
                <td className="text-center text-xs">
                  <span style={{ color: "var(--th-text-2)" }}>{p.statut}</span>
                  {p.local_status !== "none" && (
                    <span className="ml-1" style={{ color: "var(--th-text-3)" }}>· {p.local_status}</span>
                  )}
                </td>
                <td className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="p-1"
                        style={{ color: "var(--th-text-3)" }}
                        data-testid={`propale-menu-${p.id}`}
                      >
                        <MoreHorizontal size={16} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setStatus(p.id, "relance")}>Marquer comme relancée</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatus(p.id, "signe")}>Marquer gagnée</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatus(p.id, "perdu")}>Marquer perdue</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatus(p.id, "none")}>Réinitialiser</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="p-8 text-center text-sm" style={{ color: "var(--th-text-3)" }}>
                  Aucune propale.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
